/**
 * Theme information to influence the look & feel of user interface controls.
 * @property {ThemeType} [type] - Overall type of theme. Default is <code>"plain"</code>.
 * @property {ThemeMode} [mode] - Light/dark mode of the overall theme look & feel. Default is <code>"light"</code>.
 * @property {ThemeFont[]} [fonts] - Array of fonts referenced in rules.
 * @property {ThemeDesignTokens} [designTokens] - Key/value pairs of design token names and corresponding CSS values.
 * @property {ThemeRules} [rules] - CSS rules to theme specific types of UI controls.
 * @tutorial theme
 * @public
 */
export interface Theme {
  type?: ThemeType;
  mode?: ThemeMode;
  fonts?: ThemeFont[];
  designTokens?: { [key: string]: string };
  rules?: ThemeRules;
  expressButtons?: ExpressStyle;
}

/**
 * @property {ExpressButtonLayout} [buttonLayout] - Express button layout direction.
 * @property {ExpressButtonShape} [buttonShape] - Express button shape.
 * @property {number} [buttonHeight] - Express button height in pixels.
 * @property {ExpressButtonColors} [buttonColors] - Express button colors.
 * @property {ExpressButtonLabels} [buttonLabels] - Express button labels.
 * @public
 */
export interface ExpressStyle {
  buttonLayout?: ExpressButtonLayout;
  buttonShape?: ExpressButtonShape;
  buttonHeight?: number;
  buttonColors?: ExpressButtonColors;
  buttonLabels?: ExpressButtonLabels;
}

/**
 * Express button layout direction.
 * @property {string} HORIZONTAL - <code>"horizontal"</code>. Horizontal/row layout.
 * @property {string} VERTICAL - <code>"vertical"</code>. Vertical/column layout.
 * @public
 */
export enum ExpressButtonLayout {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

/**
 * Express button shape.
 * @property {string} PILL - <code>"pill"</code>. Pill shaped buttons with large button radius.
 * @property {string} RECTANGLE - <code>"rectangle"</code>. Rectangle shaped buttons with zero button radius.
 * @public
 */
export enum ExpressButtonShape {
  PILL = 'pill',
  RECTANGLE = 'rectangle',
}

/**
 * @property {ExpressButtonColorApplePay} [applepay] - Apple Pay express button color.
 * @property {ExpressButtonColorGooglePay} [googlepay] - Google Pay express button color.
 * @property {ExpressButtonColorPayPal} [paypal] - PayPal express button color.
 * @property {ExpressButtonColorVenmo} [venmo] - Venmo express button color.
 * @public
 */
export interface ExpressButtonColors {
  applepay?: ExpressButtonColorApplePay;
  googlepay?: ExpressButtonColorGooglePay;
  paypal?: ExpressButtonColorPayPal;
  venmo?: ExpressButtonColorVenmo;
}

/**
 * Express button colors for Apple Pay.
 * @property {string} BLACK - <code>"black"</code>.
 * @property {string} WHITE - <code>"white"</code>.
 * @property {string} WHITE_OUTLINE - <code>"white-outline"</code>.
 * @public
 */
export enum ExpressButtonColorApplePay {
  BLACK = 'black',
  WHITE = 'white',
  WHITE_OUTLINE = 'white-outline',
}

/**
 * Express button colors for Google Pay.
 * @property {string} BLACK - <code>"black"</code>.
 * @property {string} WHITE - <code>"white"</code>.
 * @public
 */
export enum ExpressButtonColorGooglePay {
  BLACK = 'black',
  WHITE = 'white',
}

/**
 * Express button colors for PayPal.
 * @property {string} GOLD - <code>"gold"</code>. PayPal recommended color.
 * @property {string} BLUE - <code>"blue"</code>. First alternative color.
 * @property {string} SILVER - <code>"silver"</code>. One of the second alternative colors.
 * @property {string} WHITE - <code>"white"</code>. One of the second alternative colors.
 * @property {string} BLACK - <code>"black"</code>. One of the second alternative colors.
 * @public
 */
export enum ExpressButtonColorPayPal {
  GOLD = 'gold',
  BLUE = 'blue',
  SILVER = 'silver',
  WHITE = 'white',
  BLACK = 'black',
}

/**
 * Express button colors for Venmo.
 * @property {string} BLUE - <code>"blue"</code>.
 * @property {string} SILVER - <code>"silver"</code>.
 * @property {string} WHITE - <code>"white"</code>.
 * @property {string} BLACK - <code>"black"</code>.
 * @public
 */
export enum ExpressButtonColorVenmo {
  BLUE = 'blue',
  SILVER = 'silver',
  WHITE = 'white',
  BLACK = 'black',
}

/**
 * @property {ExpressButtonLabelApplePay} [applepay] - Apple Pay express button type.
 * @property {ExpressButtonLabelGooglePay} [googlepay] - Google Pay express button type.
 * @property {ExpressButtonLabelPayPal} [paypal] - PayPal express button label.
 * @property {ExpressButtonLabelVenmo} [venmo] - Venmo express button label.
 * @public
 */
export interface ExpressButtonLabels {
  applepay?: ExpressButtonTypeApplePay;
  googlepay?: ExpressButtonTypeGooglePay;
  paypal?: ExpressButtonLabelPayPal;
  venmo?: ExpressButtonLabelVenmo;
}

/**
 * Express button types for Apple Pay.
 * @property {string} ADD_MONEY - <code>"add-money"</code>.
 * @property {string} BOOK - <code>"book"</code>.
 * @property {string} BUY - <code>"buy"</code>.
 * @property {string} CHECK_OUT - <code>"check-out"</code>.
 * @property {string} CONTRIBUTE - <code>"contribute"</code>.
 * @property {string} DONATE - <code>"donate"</code>.
 * @property {string} ORDER - <code>"order"</code>.
 * @property {string} PLAIN - <code>"plain"</code>.
 * @property {string} RELOAD - <code>"reload"</code>.
 * @property {string} RENT - <code>"rent"</code>.
 * @property {string} SUBSCRIBE - <code>"subscribe"</code>.
 * @property {string} SUPPORT - <code>"support"</code>.
 * @property {string} TIP - <code>"tip"</code>.
 * @property {string} TOP_UP - <code>"top-up"</code>.
 * @public
 */
export enum ExpressButtonTypeApplePay {
  ADD_MONEY = 'add-money',
  BOOK = 'book',
  BUY = 'buy',
  CHECK_OUT = 'check-out',
  CONTRIBUTE = 'contribute',
  DONATE = 'donate',
  ORDER = 'order',
  PLAIN = 'plain',
  RELOAD = 'reload',
  RENT = 'rent',
  SUBSCRIBE = 'subscribe',
  SUPPORT = 'support',
  TIP = 'tip',
  TOP_UP = 'top-up',
}

/**
 * Express button types for Google Pay.
 * @property {string} BOOK - <code>"book"</code>.
 * @property {string} BUY - <code>"buy"</code>.
 * @property {string} CHECKOUT - <code>"checkout"</code>.
 * @property {string} DONATE - <code>"donate"</code>.
 * @property {string} ORDER - <code>"order"</code>.
 * @property {string} PAY - <code>"pay"</code>.
 * @property {string} PLAIN - <code>"plain"</code>.
 * @property {string} SUBSCRIBE - <code>"subscribe"</code>.
 * @public
 */
export enum ExpressButtonTypeGooglePay {
  BOOK = 'book',
  BUY = 'buy',
  CHECKOUT = 'checkout',
  DONATE = 'donate',
  ORDER = 'order',
  PAY = 'pay',
  PLAIN = 'plain',
  SUBSCRIBE = 'subscribe',
}

/**
 * Express button labels for PayPal.
 * @property {string} PAYPAL - <code>"paypal"</code>. PayPal recommended label.
 * @property {string} CHECKOUT - <code>"checkout"</code>. One of the alternative labels.
 * @property {string} BUYNOW - <code>"buynow"</code>. One of the alternative labels.
 * @property {string} PAY - <code>"pay"</code>. One of the alternative labels.
 * @public
 */
export enum ExpressButtonLabelPayPal {
  PAYPAL = 'paypal',
  CHECKOUT = 'checkout',
  BUYNOW = 'buynow',
  PAY = 'pay',
}

/**
 * Express button labels for Venmo.
 * @property {string} VENMO - <code>"paypal"</code>. PayPal recommended label. Even though the enum value is <code>"paypal"</code> it results in the Venmo brand name on the button.
 * @property {string} CHECKOUT - <code>"checkout"</code>. One of the alternative labels.
 * @property {string} BUYNOW - <code>"buynow"</code>. One of the alternative labels.
 * @property {string} PAY - <code>"pay"</code>. One of the alternative labels.
 * @public
 */
export enum ExpressButtonLabelVenmo {
  VENMO = 'paypal',
  CHECKOUT = 'checkout',
  BUYNOW = 'buynow',
  PAY = 'pay',
}

/**
 * Overall type of theme. Set this value to indicate how the application intends to provide context and theme information.
 * @property {string} NONE - <code>"none"</code>. No look & feel applied by default in CSS. Applications must provide CSS to achieve a look & feel.
 * @property {string} PLAIN - <code>"plain"</code>. Plain look & feel is applied by default in CSS. Applications may provide design token values and/or override the theme CSS to influence the look & feel.
 * @tutorial theme
 * @public
 */
export enum ThemeType {
  NONE = 'none',
  PLAIN = 'plain',
}

/**
 * Light/dark mode of the overall theme. Set this value to indicate whether light or dark appropriate icons are to be used.
 * @property {string} LIGHT - <code>"light"</code>. Dark on light color scheme.
 * @property {string} DARK - <code>"dark"</code>. Light on dark color scheme, aka dark mode.
 * @tutorial theme
 * @public
 */
export enum ThemeMode {
  LIGHT = 'light',
  DARK = 'dark',
}

/**
 * Key/value pairs of design token names and corresponding CSS values. The listed properties have specific meaning, but custom properties are supported as well.
 * @property {string} linkHref - The <code>href</code> attribute that would be used in a <code><link></code> element with <code>rel="stylesheet"</code> to load <code>@font-face</code> declarations.
 * @public
 */
export interface ThemeFont {
  linkHref: string;
}

/**
 * Key/value pairs of design token names and corresponding CSS values. The listed properties have specific meaning, but custom properties are supported as well.
 * @property {string} "font-family" - Default font-family.
 * @property {string} "font-size-2" - Constant typography token for font size 2.
 * @property {string} "font-size-3" - Constant typography token for font size 3.
 * @property {string} "font-size-4" - Constant typography token for font size 4.
 * @property {string} "font-size-5" - Constant typography token for font size 5.
 * @property {string} "font-size-6" - Constant typography token for font size 6.
 * @property {string} "font-weight-regular" - Most all body copy.
 * @property {string} "font-weight-bold" - Used sparingly for emphasized text within regular body copy.
 * @property {string} "line-height-text" - Unitless line-heights for reusability.
 * @property {string} "color-text-default" - Body text color.
 * @property {string} "color-text-error" - Error text for inputs and error misc.
 * @property {string} "color-text-placeholder" - Input placeholder text.
 * @property {string} "color-text-tooltip" - Tooltip text.
 * @property {string} "color-text-weak" - Color for text that is purposefully de-emphasized to create visual hierarchy.
 * @property {string} "color-background" - Default background color for the whole app.
 * @property {string} "color-brand" - Primary brand color.
 * @property {string} "color-text-brand-primary" - Text color found on any primary brand color.
 * @property {string} "color-background-error" - Color for UI elements related to errors.
 * @property {string} "color-text-inverse" - Inverse text color for dark backgrounds.
 * @property {string} "color-background-success" - Color for UI elements that have to do with success.
 * @property {string} "color-background-tooltip" - Color for tooltip UI elements.
 * @property {string} "color-background-warning" - Color for UI elements that have to do with warning.
 * @property {string} "color-text-icon-default" - Default icon color.
 * @property {string} "color-background-row-hover" - Used as the background color for the hover state on rows or items on list-like components.
 * @property {string} "color-border-input" - Border color on form elements.
 * @property {string} "palette-neutral-75" - Neutral 75.
 * @property {string} "palette-neutral-95" - Neutral 95.
 * @property {string} "border-radius-medium" - Icons in lists, record home icon, unbound input elements.
 * @property {string} "border-radius-small" - Checkboxes.
 * @property {string} "spacing-large" - Constant spacing token for size Large.
 * @property {string} "spacing-medium" - Constant spacing token for size Medium.
 * @property {string} "spacing-small" - Constant spacing token for size Small.
 * @property {string} "spacing-x-large" - Constant spacing token for size X Large.
 * @property {string} "spacing-x-small" - Constant spacing token for size X Small.
 * @property {string} "spacing-xx-small" - Constant spacing token for size XX Small.
 * @property {string} "spacing-xxx-small" - Constant spacing token for size XXX Small.
 * @tutorial theme
 * @public
 */
export interface ThemeDesignTokens {
  'font-family'?: string;
  'font-size-2'?: string;
  'font-size-3'?: string;
  'font-size-4'?: string;
  'font-size-5'?: string;
  'font-size-6'?: string;
  'font-weight-regular'?: string;
  'font-weight-bold'?: string;
  'line-height-text'?: string;
  'color-text-default'?: string;
  'color-text-error'?: string;
  'color-text-placeholder'?: string;
  'color-text-tooltip'?: string;
  'color-text-weak'?: string;
  'color-background'?: string;
  'color-brand'?: string;
  'color-text-brand-primary'?: string;
  'color-background-error'?: string;
  'color-text-inverse'?: string;
  'color-background-success'?: string;
  'color-background-tooltip'?: string;
  'color-background-warning'?: string;
  'color-text-icon-default'?: string;
  'color-background-row-hover'?: string;
  'color-border-input'?: string;
  'palette-neutral-95'?: string;
  'palette-neutral-75'?: string;
  'border-radius-medium'?: string;
  'border-radius-small'?: string;
  'spacing-large'?: string;
  'spacing-medium'?: string;
  'spacing-small'?: string;
  'spacing-x-large'?: string;
  'spacing-x-small'?: string;
  'spacing-xx-small'?: string;
  'spacing-xxx-small'?: string;
  [customName: string]: string;
}

/**
 * CSS rules to theme specific types of UI controls. Nested rules represent states like "invalid" and/or pseudoclasses like "hover", sometimes in combination.
 * @property {ThemeRule} formLabel - CSS rule for the label of a form field.
 * @property {ThemeRule} formLabel.invalid - CSS rule for the label of a form field in invalid state.
 * @property {ThemeRule} input - CSS rule for a form input field.
 * @property {ThemeRule} input.focus - CSS rule for a form input field with focus pseudoclass.
 * @property {ThemeRule} input.hover - CSS rule for a form input field with hover pseudoclass.
 * @property {ThemeRule} input.invalid - CSS rule for a form input field in invalid state.
 * @property {ThemeRule} input.invalid.focus - CSS rule for a form input field in invalid state with focus pseudoclass.
 * @property {ThemeRule} input.invalid.hover - CSS rule for a form input field in invalid state with hover pseudoclass.
 * @property {ThemeRule} checkbox - CSS rule for a checkbox input.
 * @property {ThemeRule} checkbox.hover - CSS rule for a checkbox input with hover pseudoclass.
 * @property {ThemeRule} checkboxLabel - CSS rule for the label of a checkbox.
 * @property {ThemeRule} checkboxLabel.hover - CSS rule for the label of a checkbox with hover pseudoclass.
 * @property {ThemeRule} button - CSS rule for a button.
 * @property {ThemeRule} button.focus - CSS rule for a button with focus pseudoclass.
 * @property {ThemeRule} button.hover - CSS rule for a button with hover pseudoclass.
 * @property {ThemeRule} button.active - CSS rule for a button with active pseudoclass.
 * @property {ThemeRule} picklist - CSS rule for a picklist.
 * @property {ThemeRule} picklistOption - CSS rule for an option in the listbox for an open picklist.
 * @property {ThemeRule} picklistOption.hover - CSS rule for an option in the listbox for an open picklist with hover pseudoclass.
 * @property {ThemeRule} error - CSS rule for the inline error message of a form field in invalid state.
 * @property {ThemeRule} card - CSS rule for a card panel.
 * @tutorial theme
 */
export interface ThemeRules {
  formLabel?: ThemeRule & {
    invalid?: ThemeRule;
  };
  input?: ThemeRule & {
    focus?: ThemeRule;
    hover?: ThemeRule;
    invalid?: ThemeRule & {
      focus?: ThemeRule;
      hover?: ThemeRule;
    };
  };
  checkbox?: ThemeRule & {
    hover?: ThemeRule;
    focus?: ThemeRule;
  };
  checkboxLabel?: ThemeRule & {
    hover?: ThemeRule;
  };
  button?: ThemeRule & {
    focus?: ThemeRule;
    hover?: ThemeRule;
    active?: ThemeRule;
  };
  picklist?: ThemeRule;
  picklistOption?: ThemeRule & {
    hover?: ThemeRule;
  };
  error?: ThemeRule;
  card?: ThemeRule;
}

/**
 * CSS rule to theme a UI control.
 * @property {string} background-color - CSS <code>background-color</code> property.
 * @property {string} border - CSS <code>border</code> property.
 * @property {string} border-radius - CSS <code>border-radius</code> property.
 * @property {string} box-shadow - CSS <code>box-shadow</code> property.
 * @property {string} color - CSS <code>color</code> property.
 * @property {string} fill - CSS <code>fill</code> property.
 * @property {string} font-family - CSS <code>font-family</code> property.
 * @property {string} font-size - CSS <code>font-size</code> property.
 * @property {string} font-variant - CSS <code>font-variant</code> property.
 * @property {string} font-variation - CSS <code>font-variation</code> property.
 * @property {string} font-weight - CSS <code>font-weight</code> property.
 * @property {string} letter-spacing - CSS <code>letter-spacing</code> property.
 * @property {string} line-height - CSS <code>line-height</code> property.
 * @property {string} margin - CSS <code>margin</code> property.
 * @property {string} opacity - CSS <code>opacity</code> property.
 * @property {string} outline - CSS <code>outline</code> property.
 * @property {string} padding - CSS <code>padding</code> property.
 * @property {string} text-decoration - CSS <code>text-decoration</code> property.
 * @property {string} text-shadow - CSS <code>text-shadow</code> property.
 * @property {string} text-transform - CSS <code>text-transform</code> property.
 * @property {string} transition - CSS <code>transition</code> property.
 * @tutorial theme
 * @public
 */
export interface ThemeRule {
  'background-color'?: string;
  border?: string;
  'border-radius'?: string;
  'box-shadow'?: string;
  color?: string;
  fill?: string;
  'font-family'?: string;
  'font-size'?: string;
  'font-variant'?: string;
  'font-variation'?: string;
  'font-weight'?: string;
  'letter-spacing'?: string;
  'line-height'?: string;
  margin?: string;
  opacity?: string;
  outline?: string;
  padding?: string;
  'text-decoration'?: string;
  'text-shadow'?: string;
  'text-transform'?: string;
  transition?: string;
}
